import * as React from "react"
import { ArrowLeft, X } from "lucide-react"
import { useIsMobile } from "@/hooks/use-mobile"
import { cn } from "@/lib/utils"
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from "./dialog"
import {
  Sheet,
  SheetTrigger,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
  SheetClose,
} from "./sheet"

type Side = "top" | "right" | "bottom" | "left"

const ResponsiveDialogContext = React.createContext<boolean | null>(null)

function useResponsiveIsMobile(): boolean {
  const ctx = React.useContext(ResponsiveDialogContext)
  // Fallback: if a subcomponent is used outside ResponsiveDialog, behave as mobile-aware standalone.
  // This shouldn't normally happen but keeps the component safe.
  const standaloneIsMobile = useIsMobile()
  return ctx ?? standaloneIsMobile
}

function ResponsiveDialog(props: React.ComponentProps<typeof Dialog>) {
  const isMobile = useIsMobile()
  const Root = isMobile ? Sheet : Dialog
  return (
    <ResponsiveDialogContext.Provider value={isMobile}>
      <Root {...props} />
    </ResponsiveDialogContext.Provider>
  )
}

function ResponsiveDialogTrigger(props: React.ComponentProps<typeof DialogTrigger>) {
  const isMobile = useResponsiveIsMobile()
  const Trigger = isMobile ? SheetTrigger : DialogTrigger
  return <Trigger {...props} />
}

interface ResponsiveDialogContentProps extends React.ComponentProps<typeof DialogContent> {
  /** Sheet `side` em mobile. Ignorado em desktop. Default: `"bottom"`. */
  side?: Side
  /** Mobile-only. Renderiza handle bar (alça de drawer) no topo do Sheet. Default: `true`. Ignorado em desktop. */
  showHandle?: boolean
  /** Esconde o close button — em desktop propaga para `DialogContent.showCloseButton`; em mobile esconde o close hardcoded do SheetContent via CSS. Default: `true`. */
  showCloseButton?: boolean
}

function ResponsiveDialogContent({
  className,
  children,
  side = "bottom",
  showHandle = true,
  showCloseButton = true,
  ...props
}: ResponsiveDialogContentProps) {
  const isMobile = useResponsiveIsMobile()

  if (isMobile) {
    return (
      <>
        {/* Rendered outside the portal so tests can find it via container.querySelector */}
        {!showCloseButton && (
          <span hidden data-responsive-hide-close="true" />
        )}
        <SheetContent
          side={side}
          className={cn(
            // Layout: o SheetContent vira flex column SEM scroll próprio.
            // O scroll fica numa div interna (`.rd-body`), pra que:
            //   - o handle/close (X) fiquem fixos no topo do sheet;
            //   - o ResponsiveDialogFooter (`sticky bottom-0`) consiga pinnar
            //     na borda inferior do scroll container (sem ser engolido por
            //     containing-block de form/fieldset que quebra sticky).
            "p-0 max-h-[90vh] flex flex-col gap-0",
            // Esconde o close button absolute do Sheet base (último filho).
            // O X agora é renderizado dentro do ResponsiveDialogHeader pra
            // ficar alinhado com o título + back arrow.
            "[&>button:last-child]:hidden",
            className
          )}
          {...props}
        >
          {/* `showHandle` virou no-op: o "handle" de fechar virou o ArrowLeft
              dentro do ResponsiveDialogHeader, mais visível. Prop mantida pra compat. */}
          {showHandle === undefined && null}
          {/* px-4 mantém padding lateral pro conteúdo; py controlado pelo
              header/footer sticky (que precisam reach body's outer top/bottom
              edges sem deixar uma faixa de padding sobrando — sticky offsets
              são calculados a partir do padding-edge do scroll container). */}
          <div data-slot="responsive-dialog-body" className="flex-1 min-h-0 overflow-y-auto px-4">
            {children}
          </div>
        </SheetContent>
      </>
    )
  }

  return (
    <DialogContent
      showCloseButton={showCloseButton}
      className={className}
      {...props}
    >
      {children}
    </DialogContent>
  )
}

function ResponsiveDialogHeader({ className, children, ...props }: React.ComponentProps<typeof DialogHeader>) {
  const isMobile = useResponsiveIsMobile()
  if (isMobile) {
    // Row layout: [← Voltar] [Título flex-1] [X Fechar]
    // -mx-4 px-4 estende o header até as bordas (compensa o px-4 do body).
    // !flex-row sobrepõe o flex-col default do SheetHeader.
    return (
      <SheetHeader
        className={cn(
          "sticky top-0 z-10 bg-background -mx-4 px-4 pt-3 pb-3 border-b",
          "!flex-row !gap-2 items-center",
          className
        )}
        {...props}
      >
        <SheetClose asChild>
          <button
            type="button"
            aria-label="Voltar"
            data-slot="responsive-dialog-back"
            className="-ml-2 p-2 rounded hover:bg-muted shrink-0"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
        </SheetClose>
        <div className="flex-1 min-w-0 flex flex-col gap-0.5">
          {children}
        </div>
        <SheetClose asChild>
          <button
            type="button"
            aria-label="Fechar"
            data-slot="responsive-dialog-close"
            className="-mr-2 p-2 rounded hover:bg-muted shrink-0 opacity-70 hover:opacity-100"
          >
            <X className="h-5 w-5" />
          </button>
        </SheetClose>
      </SheetHeader>
    )
  }
  return <DialogHeader className={className} {...props}>{children}</DialogHeader>
}

function ResponsiveDialogTitle(props: React.ComponentProps<typeof DialogTitle>) {
  const isMobile = useResponsiveIsMobile()
  const Title = isMobile ? SheetTitle : DialogTitle
  return <Title {...props} />
}

function ResponsiveDialogDescription(props: React.ComponentProps<typeof DialogDescription>) {
  const isMobile = useResponsiveIsMobile()
  const Description = isMobile ? SheetDescription : DialogDescription
  return <Description {...props} />
}

function ResponsiveDialogFooter({ className, ...props }: React.ComponentProps<typeof DialogFooter>) {
  const isMobile = useResponsiveIsMobile()
  if (isMobile) {
    // -mx-4 px-4 estende o footer pelas bordas (compensa o px-4 do body, que
    // já não tem padding vertical pra sticky bottom-0 alcançar a borda inferior).
    return (
      <SheetFooter
        className={cn(
          "sticky bottom-0 bg-background border-t -mx-4 px-4",
          className
        )}
        {...props}
      />
    )
  }
  return <DialogFooter className={className} {...props} />
}

function ResponsiveDialogClose(props: React.ComponentProps<typeof DialogClose>) {
  const isMobile = useResponsiveIsMobile()
  const Close = isMobile ? SheetClose : DialogClose
  return <Close {...props} />
}

export {
  ResponsiveDialog,
  ResponsiveDialogTrigger,
  ResponsiveDialogContent,
  ResponsiveDialogHeader,
  ResponsiveDialogTitle,
  ResponsiveDialogDescription,
  ResponsiveDialogFooter,
  ResponsiveDialogClose,
}
